import { Header, User } from "@yy/yycom_header_types";
import Cookies from "js-cookie";
import React, { useContext } from "react";
import { BrowserRouter, HashRouter } from "react-router-dom";
import Boards from "./components/Boards";
import ChannelIdContextWrap from "./components/ChannelIdContextWrap";
import ModalSwitch from "./components/ModalSwitch/ModalSwitch";
import fetcher, { paths } from "./lib/fetcher";
import { ExposeLandingPageHiidoPV } from "./lib/hooks/useHiidoPV";
import { HTTPService, UserInfoResultData, UserRole } from "./lib/util";
import { CERTStatusResult } from "./models/CertStatusResult";
import { SettleSwitchResult } from "./models/SettleSwitchResult";
import { UserRoleResult } from "./models/UserRoleResult";
import "./style/App.scss";

interface HeadLoaderOnReadyCallbackOption {
  header: Header;
  user: User;
}

export const LoginStatusContext = React.createContext<Boolean>(false);
export const UserRoleContext = React.createContext<UserRole>(0);
export const CertStatusContext = React.createContext<{ isCert: boolean }>({ isCert: false });
export const UserInfoContext = React.createContext<Partial<UserInfoResultData>>({});
export const SettleSwitchContext = React.createContext<boolean>(true); // 默认开启

/**
 * 根据环境切换路由模式：
 * 本地开发使用 hash 模式，
 * 其他（测试环境，生产环境）使用 browser 模式
 */
const devMode = process.env.NODE_ENV === "development";
const Router: React.ElementType = devMode ? HashRouter : BrowserRouter;
const basename = devMode ? undefined : "/gh/cl";

interface AppState {
  isLoggedIn: boolean;
  userRole: UserRole;
  isCert: boolean;
  loading: boolean;
  userInfo: Partial<UserInfoResultData>;
  isSettleSwitchOn: boolean;
}

export default class App extends React.Component {
  // 获取百度开关
  fetchSettleSwitch = async () => {
    const { code, data } = (await fetcher.get(paths.user.settleSwitch)) as SettleSwitchResult;
    if (code === HTTPService.Ok) {
      this.setState({ isSettleSwitchOn: !!data });
    } else {
      throw new Error("获取百度开关失败");
    }
  };

  fetchUserInfo = async () => {
    const { code, data, message } = (await fetcher.get(paths.user.userInfo)) as UserRoleResult;
    if (code === HTTPService.Ok) {
      this.setState({ userInfo: data });
    } else {
      throw new Error(message);
    }
  };

  fetchUserRole = async () => {
    const { code, data, message } = (await fetcher.get(paths.user.userRole)) as UserRoleResult;

    if (code === HTTPService.Ok) {
      this.setState({ userRole: data });
    } else {
      throw new Error(message);
    }
  };

  static isLoggedIn = () => {
    let result = Cookies.get("udb_n") && Cookies.get("udb_l") && Cookies.get("yyuid");
    return !!result;
  };

  state: AppState = {
    isLoggedIn: App.isLoggedIn(),
    userRole: UserRole.Unknown,
    userInfo: {},
    isCert: false,
    loading: true,
    isSettleSwitchOn: true,
  };

  static onHeaderReady = (): Promise<HeadLoaderOnReadyCallbackOption> =>
    /**
     * Initialize header
     * http://fet.yy.com/widget/header-api/#typescript-%E6%96%B9%E5%BC%8F
     */
    new Promise((resolve) => {
      window.headLoader.onReady((egg) => {
        resolve(egg);
      });
    });

  fetchInitData = async () => {
    this.fetchUserInfo();
    this.fetchSettleSwitch();
    await this.fetchUserRole();
    await this.fetchCertStatus();
  };

  // [0, 1, 2] 已完成实名
  fetchCertStatus = async () => {
    const { code } = (await fetcher.get(paths.certStatus)) as CERTStatusResult;
    this.setState({
      isCert: [0, 1, 2].includes(code),
    });
  };

  initialize = async () => {
    // const { header, user } = await this.onHeaderReady();
    const { header } = await App.onHeaderReady();

    // 初始化header
    header({ target: "#h", fixed: true });

    // // 如果没登录，弹出登录框
    // if (!App.isLoggedIn()) {
    //   user.showYYLoginBox();
    // }

    if (App.isLoggedIn()) {
      await this.fetchInitData();
      this.setState({ loading: false });
    } else {
      this.setState({ loading: false });
    }

    // 监听登录状态变化
    header.onLogStateChange(async (isLoggedIn) => {
      if (isLoggedIn) {
        await this.fetchInitData();
        // if (!this.state.isCert) {
        // this.state.CertModalVisible = true;
        // }
      } else {
        window.location.reload();
      }

      this.setState({ isLoggedIn });
    });
  };

  componentDidMount() {
    this.initialize();
  }

  render() {
    const { isLoggedIn, userRole, loading, isCert, userInfo, isSettleSwitchOn } = this.state;

    return (
      <LoginStatusContext.Provider value={isLoggedIn}>
        <UserRoleContext.Provider value={userRole}>
          <UserInfoContext.Provider value={userInfo}>
            <CertStatusContext.Provider value={{ isCert }}>
              <SettleSwitchContext.Provider value={isSettleSwitchOn}>
                <Router basename={basename}>
                  <ChannelIdContextWrap>
                    {!loading && <Boards />}
                    {/* 曝光落地页PV */}
                    <ExposeLandingPageHiidoPV />
                    {/* 使用 react router 管理各种弹窗  */}
                    {/* 参考例子: https://reactrouter.com/web/example/modal-gallery */}
                    <ModalSwitch />
                  </ChannelIdContextWrap>
                </Router>
              </SettleSwitchContext.Provider>
            </CertStatusContext.Provider>
          </UserInfoContext.Provider>
        </UserRoleContext.Provider>
      </LoginStatusContext.Provider>
    );
  }
}
